(() => {
  class YuanFrequentlyBought extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.track = this.querySelector('[data-yuan-fbt-track]');
      this.prevButton = this.querySelector('[data-yuan-fbt-nav="prev"]');
      this.nextButton = this.querySelector('[data-yuan-fbt-nav="next"]');
      this.addButton = this.querySelector('[data-yuan-fbt-add]');
      this.addText = this.querySelector('[data-yuan-fbt-add-text]');
      this.message = this.querySelector('[data-yuan-fbt-message]');

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.updateSummary();
      this.updateNav();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.dataset.initialized = 'false';
    }

    onChange(event) {
      const checkbox = event.target.closest('[data-yuan-fbt-check]');
      const variantSelect = event.target.closest('[data-yuan-fbt-variant]');

      if (checkbox) {
        const card = checkbox.closest('[data-yuan-fbt-item]');
        card?.classList.toggle('is-selected', checkbox.checked);
        this.updateSummary();
        return;
      }

      if (variantSelect) {
        this.selectVariant(variantSelect);
      }
    }

    onClick(event) {
      const nav = event.target.closest('[data-yuan-fbt-nav]');
      if (nav) {
        this.scrollByPage(nav.dataset.yuanFbtNav);
        return;
      }

      if (event.target.closest('[data-yuan-fbt-add]')) {
        this.addSelected();
      }
    }

    onScroll() {
      this.updateNav();
    }

    onResize() {
      this.updateNav();
    }

    selectVariant(select) {
      const card = select.closest('[data-yuan-fbt-item]');
      const option = select.selectedOptions[0];
      if (!card || !option) return;

      const available = option.dataset.available === 'true';
      card.dataset.variantId = option.value;
      card.dataset.price = option.dataset.price;
      card.dataset.available = String(available);
      card.classList.toggle('is-unavailable', !available);

      const priceEl = card.querySelector('[data-yuan-fbt-price]');
      if (priceEl) priceEl.textContent = option.dataset.formattedPrice;

      const checkbox = card.querySelector('[data-yuan-fbt-check]');
      if (checkbox) {
        checkbox.disabled = !available;
        if (!available) {
          checkbox.checked = false;
          card.classList.remove('is-selected');
        }
      }

      this.updateSummary();
    }

    selectedItems() {
      return [...this.querySelectorAll('[data-yuan-fbt-item]')].flatMap((card) => {
        const checkbox = card.querySelector('[data-yuan-fbt-check]');
        if (!checkbox?.checked || card.dataset.available === 'false') return [];
        return [{
          id: Number(card.dataset.variantId),
          quantity: 1,
          price: Number(card.dataset.price) || 0,
        }];
      });
    }

    updateSummary() {
      const selected = this.selectedItems();
      const count = selected.length;
      const totalCents = selected.reduce((sum, item) => sum + item.price, 0);

      this.addButton.disabled = count === 0;
      this.addText.textContent = count === 0
        ? this.dataset.emptyLabel
        : this.dataset.buyLabel
          .replace('__COUNT__', this.formatCount(count))
          .replace('__TOTAL__', this.formatMoney(totalCents));
    }

    formatCount(count) {
      return new Intl.NumberFormat(document.documentElement.lang || 'ar').format(count);
    }

    formatMoney(cents) {
      const amount = cents / 100;
      const formatted = new Intl.NumberFormat('ar-EG', {
        maximumFractionDigits: 2,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
      return `${formatted} ج.م`;
    }

    scrollByPage(direction) {
      if (!this.track) return;
      const amount = Math.max(this.track.clientWidth * 0.75, 160);
      const rtl = getComputedStyle(this).direction === 'rtl';
      const delta = direction === 'next' ? amount : -amount;
      this.track.scrollBy({ left: rtl ? -delta : delta, behavior: 'smooth' });
    }

    updateNav() {
      if (!this.track || !this.prevButton || !this.nextButton) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const canScroll = maxScroll > 8;

      if (!canScroll) {
        this.prevButton.hidden = true;
        this.nextButton.hidden = true;
        return;
      }

      const rtl = getComputedStyle(this).direction === 'rtl';
      const scrollLeft = Math.abs(this.track.scrollLeft);
      const atStart = scrollLeft <= 8;
      const atEnd = scrollLeft >= maxScroll - 8;

      this.prevButton.hidden = false;
      this.nextButton.hidden = false;
      this.prevButton.disabled = rtl ? atEnd : atStart;
      this.nextButton.disabled = rtl ? atStart : atEnd;
    }

    async addSelected() {
      const items = this.selectedItems().map(({ id, quantity }) => ({ id, quantity }));
      if (items.length === 0) return;

      const cartSurface = document.querySelector('cart-drawer');
      this.addButton.disabled = true;
      this.addButton.classList.add('is-loading');
      this.message.hidden = true;
      this.message.classList.remove('is-success');

      try {
        const requestBody = { items };
        requestBody.sections = cartSurface
          ? cartSurface.getSectionsToRender().map((section) => section.id)
          : ['cart-icon-bubble'];
        requestBody.sections_url = window.location.pathname;
        cartSurface?.setActiveElement(document.activeElement);

        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(requestBody),
        });
        const cartResponse = await response.json();
        if (!response.ok || cartResponse.status) {
          throw new Error(cartResponse.description || cartResponse.message || window.cartStrings?.error || 'Error');
        }

        if (cartSurface) {
          cartSurface.classList.remove('is-empty');
          cartSurface.renderContents(cartResponse);
        } else {
          this.updateCartIcon(cartResponse.sections?.['cart-icon-bubble']);
          this.message.textContent = this.dataset.successLabel;
          this.message.classList.add('is-success');
          this.message.hidden = false;
        }
      } catch (error) {
        this.message.textContent = error.message || window.cartStrings?.error || 'Error';
        this.message.hidden = false;
      } finally {
        this.addButton.classList.remove('is-loading');
        this.updateSummary();
      }
    }

    updateCartIcon(sectionHtml) {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (!sectionHtml || !cartIcon) {
        window.location.assign(routes.cart_url);
        return;
      }
      const sectionRoot = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('.shopify-section');
      if (!sectionRoot) {
        window.location.assign(routes.cart_url);
        return;
      }
      cartIcon.innerHTML = sectionRoot.innerHTML;
    }
  }

  if (!customElements.get('yuan-frequently-bought')) {
    customElements.define('yuan-frequently-bought', YuanFrequentlyBought);
  }
})();
